import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Tag, Copy } from "lucide-react";
import api, { fmtJMD, formatApiError } from "../../lib/api";

const BLANK = { code: "", type: "percent", value: 10, min_order: 0, usage_limit: null, expires_at: "", active: true, description: "" };

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => setCoupons((await api.get("/coupons")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.code.trim()) { toast.error("Code required"); return; }
    setSaving(true);
    try {
      const payload = { ...editing };
      if (payload.usage_limit === "" || payload.usage_limit === null) payload.usage_limit = null;
      else payload.usage_limit = Number(payload.usage_limit);
      payload.value = Number(payload.value);
      payload.min_order = Number(payload.min_order || 0);
      if (editing.id) {
        await api.patch(`/coupons/${editing.id}`, payload);
      } else {
        await api.post("/coupons", payload);
      }
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    try { await api.delete(`/coupons/${c.id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success(`Copied ${code}`);
  };

  return (
    <div data-testid="admin-coupons">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="overline text-zinc-500">Marketing</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Coupon codes</h1>
        </div>
        <button onClick={() => setEditing({ ...BLANK })} className="btn-primary inline-flex items-center gap-2" data-testid="new-coupon-btn">
          <Plus size={16} /> New coupon
        </button>
      </div>

      {coupons.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-200 p-10 text-center">
          <Tag size={28} className="mx-auto text-zinc-300 mb-3" />
          <p className="text-sm text-zinc-500">No coupons yet — create your first promo above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {coupons.map((c) => (
            <div key={c.id} className={`rounded-2xl border-2 border-dashed p-5 ${c.active ? "border-[#F74B64] bg-[#FFF5F6]" : "border-zinc-200 bg-zinc-50 opacity-70"}`}
                 data-testid={`coupon-${c.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <button onClick={() => copyCode(c.code)} className="font-display font-bold text-lg inline-flex items-center gap-1 hover:opacity-80">
                  {c.code} <Copy size={12} className="text-zinc-400" />
                </button>
                <div className="flex gap-1">
                  <button onClick={() => setEditing(c)} className="p-1.5 hover:bg-white rounded" data-testid={`edit-coupon-${c.id}`}>
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => remove(c)} className="p-1.5 hover:bg-red-50 text-red-500 rounded" data-testid={`delete-coupon-${c.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="text-sm font-semibold">
                {c.type === "percent" ? `${c.value}% off` : `${fmtJMD(c.value)} off`}
                {c.min_order > 0 && <span className="text-xs text-zinc-500"> · min {fmtJMD(c.min_order)}</span>}
              </div>
              {c.description && <p className="text-xs text-zinc-500 mt-1 break-words">{c.description}</p>}
              <div className="flex items-center justify-between text-xs text-zinc-500 mt-3 pt-3 border-t border-zinc-200">
                <span>Used {c.used_count}{c.usage_limit ? ` / ${c.usage_limit}` : ""}</span>
                {c.expires_at && <span>Expires {c.expires_at.slice(0, 10)}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-5 sm:p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-bold text-lg">{editing.id ? "Edit coupon" : "New coupon"}</div>
              <button onClick={() => setEditing(null)} className="p-1 hover:bg-zinc-100 rounded"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <F label="Code">
                <input className="input uppercase" value={editing.code}
                       onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })}
                       data-testid="coupon-code" />
              </F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Type">
                  <select className="input" value={editing.type}
                          onChange={(e) => setEditing({ ...editing, type: e.target.value })}
                          data-testid="coupon-type">
                    <option value="percent">Percent</option>
                    <option value="fixed">Fixed JMD</option>
                  </select>
                </F>
                <F label={editing.type === "percent" ? "Value (%)" : "Value (JMD)"}>
                  <input className="input" type="number" value={editing.value}
                         onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                         data-testid="coupon-value" />
                </F>
              </div>
              <F label="Minimum order (JMD)">
                <input className="input" type="number" value={editing.min_order}
                       onChange={(e) => setEditing({ ...editing, min_order: e.target.value })}
                       data-testid="coupon-min" />
              </F>
              <F label="Usage limit (blank = unlimited)">
                <input className="input" type="number" value={editing.usage_limit ?? ""}
                       onChange={(e) => setEditing({ ...editing, usage_limit: e.target.value })}
                       data-testid="coupon-limit" />
              </F>
              <F label="Expires at (optional)">
                <input className="input" type="datetime-local" value={editing.expires_at?.slice(0, 16) || ""}
                       onChange={(e) => setEditing({ ...editing, expires_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                       data-testid="coupon-expires" />
              </F>
              <F label="Description (internal)">
                <input className="input" value={editing.description || ""}
                       onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                       data-testid="coupon-description" />
              </F>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.active}
                       onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
                Active
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50" data-testid="save-coupon-btn">
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
      <style>{`.input { width:100%; border:1px solid #e4e4e7; border-radius:0.5rem; padding:0.5rem 0.75rem; font-size:0.875rem; }`}</style>
    </div>
  );
}

function F({ label, children }) {
  return <div><label className="text-xs font-semibold text-zinc-600 block mb-1">{label}</label>{children}</div>;
}
